import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { themePalette } from "../../theme";
import { Button } from "../shared/Button/Button";
import ShipKitToPatientForm, {
  ShipKitShippingInfo,
} from "./ShipKitToPatientForm";
import { NewRequisitionType } from "./types";
import {
  NewReqRequiredMessage,
  PatientInformationBox,
  StepperPanelDesktop,
} from "./NewRequisition.styles";
import { TextWrap } from "../Grid/Grid.styles";
import { shipKitToPatient } from "./Services/NewReqService";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";
import { SpinnerLoading } from "../shared/Spinner/SpinnerLoading";
import { WarningIcon } from "../../assets/iconComponents/Warning";

const ShipKitToPatientModal: React.FC<{
  open: boolean;
  onClose: () => void;
  orderData: NewRequisitionType;
  onSuccess: () => void;
}> = ({ open, onClose, orderData, onSuccess }) => {
  const { auth, setLoggedOut } = useAuthenticationHook();
  const patientAddress: ShipKitShippingInfo = {
    firstName: orderData.first_name,
    lastName: orderData.last_name,
    fullAddress: "",
    streetAddress: orderData.street_address,
    aptUnit: orderData.apt_suite,
    city: orderData.city,
    state: orderData.state,
    zipCode: orderData.zip_code,
  };
  const [sameAsPatientAddress, setSameAsPatientAddress] = useState(true);
  const [shippingInfo, setShippingInfo] =
    useState<ShipKitShippingInfo>(patientAddress);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  useEffect(() => {
    if (open) {
      setSameAsPatientAddress(true);
      setShippingInfo(patientAddress);
      setSubmitError(false);
    }
  }, [open, orderData]);

  const effectiveInfo = sameAsPatientAddress ? patientAddress : shippingInfo;

  const handlePlaceOrder = async () => {
    if (!auth) {
      return;
    }
    setLoading(true);
    const success = await shipKitToPatient(
      orderData.unity_complete_id,
      orderData.is_mobile_phlebotomy,
      {
        first_name: effectiveInfo.firstName,
        last_name: effectiveInfo.lastName,
        street_address: effectiveInfo.streetAddress,
        apt_suite: effectiveInfo.aptUnit,
        city: effectiveInfo.city,
        state: effectiveInfo.state.split("-")[0].trim(),
        zip_code: effectiveInfo.zipCode,
      },
      auth.token,
      setLoggedOut,
    );
    setLoading(false);
    if (success) {
      onSuccess();
      onClose();
    } else {
      setSubmitError(true);
    }
  };

  return (
    <Dialog id="ship-kit-dialog" fullWidth maxWidth="md" open={open}>
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          backgroundColor: themePalette.neutral50,
          borderBottom: `1px solid ${themePalette.neutral100}`,
        }}
      >
        <TextWrap>SHIP KIT TO PATIENT</TextWrap>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: themePalette.redMedium,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ overflowX: "hidden" }}>
        <PatientInformationBox id="top">
          {submitError && (
            <NewReqRequiredMessage $isError={submitError}>
              <WarningIcon testId="WarningIcon" />
              There was an error submitting the order. If the error persists,
              please contact support at (650)-460-2551.
            </NewReqRequiredMessage>
          )}
          <ShipKitToPatientForm
            mode="standalone"
            shipToPatient
            onShipToPatientChange={() => {}}
            sameAsPatientAddress={sameAsPatientAddress}
            onSameAsPatientToggle={setSameAsPatientAddress}
            shippingInfo={shippingInfo}
            onShippingInfoChange={setShippingInfo}
            patientAddress={patientAddress}
          />
        </PatientInformationBox>
      </DialogContent>
      <StepperPanelDesktop>
        <Button label="CANCEL" reverse size="medium" onClick={onClose} />
        {loading ? (
          <SpinnerLoading />
        ) : (
          <Button
            id="ship-kit-place-order"
            label="PLACE ORDER"
            size="medium"
            onClick={handlePlaceOrder}
          />
        )}
      </StepperPanelDesktop>
    </Dialog>
  );
};

export default ShipKitToPatientModal;
